/**
 * Token-shape detector for machine-connected services (docs/specs/machine-connected-services.md §4). A probe's output is
 * checked before anything from it is kept: a value that looks like a credential is dropped, never stored or shown.
 *
 * It reports the shape's name, never the matched text, so a caller can log "github_pat" without logging the token.
 * Broader than `secrets.ts`' `looksLikeSecret` on purpose: it also covers `gh*_` prefixes, Bearer headers and long
 * base64 or hex runs. A masked value such as `gho_****` is not a token and does not match.
 */

export const TOKEN_SHAPES = [
  { name: "github_token", re: /(?<![A-Za-z0-9])gh[pousr]_[A-Za-z0-9]{20,}(?![A-Za-z0-9])/ },
  { name: "github_pat", re: /(?<![A-Za-z0-9_])github_pat_[A-Za-z0-9_]{20,}/ },
  { name: "jwt", re: /(?<![A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/ },
  { name: "bearer", re: /(?<![A-Za-z0-9_-])Bearer\s+[A-Za-z0-9._~+/=-]{8,}/i },
  { name: "long_base64", re: /(?<![A-Za-z0-9+/=_-])[A-Za-z0-9+/_-]{40,}={0,2}(?![A-Za-z0-9+/=_-])/ },
  { name: "long_hex", re: /(?<![A-Za-z0-9])[A-Fa-f0-9]{32,}(?![A-Za-z0-9])/ },
] as const;

export type TokenShapeName = (typeof TOKEN_SHAPES)[number]["name"];

/** The first shape `text` matches, or null. Returns the shape's name only. */
export function findTokenShape(text: string): TokenShapeName | null {
  for (const shape of TOKEN_SHAPES) if (shape.re.test(text)) return shape.name;
  return null;
}

const CREDENTIAL_SHAPES = TOKEN_SHAPES.filter((s) => s.name === "github_token" || s.name === "github_pat" || s.name === "jwt" || s.name === "bearer").map(
  (s) => new RegExp(s.re.source, `${s.re.flags.replace("g", "")}g`),
);

/**
 * Masks credentials in free text that is stored or sent (a push-check message, a failure reason): GitHub tokens, JWTs,
 * `Bearer …`, URL userinfo and `x-access-token` values. Unlike the full detector it leaves long hex runs alone, so a git
 * sha in a message stays readable.
 */
export function redactCredentials(text: string): string {
  let out = text;
  for (const re of CREDENTIAL_SHAPES) out = out.replace(re, "[redacted]");
  return out.replace(/(\/\/)[^/\s:@]+:[^/\s@]*@/g, "$1[redacted]@").replace(/(x-access-token[:=\s]+)\S+/gi, "$1[redacted]");
}

/** True when `text` contains any token-shaped run. */
export function looksLikeToken(text: string): boolean {
  return findTokenShape(text) !== null;
}
